import React from "react"
import Layout from "../../components/layout"
import { Link, withPrefix } from "gatsby"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import LendingMap from "../../components/lending-map"
import ReadNext from "../../components/read-next"

const PAGESLUG = "lending"

export default function Lending() {
  return (
    <Layout>
      <section className="page page--pullBottom page--margin-large">
        <Hero title="Lending" image="what-we-do/lending/hero.jpg" />

        <div className="page__intro container md:container--small text-block">
          <p className="dropcap">
            <b>IMF financing is meant to help member countries</b> tackle
            balance of payments problems, stabilize their economies, and restore
            sustainable economic growth. Unlike development banks, the IMF does
            not lend for specific projects. IMF financing can also be provided
            in response to natural disasters or pandemics. Finally, the IMF also
            provides precautionary financing to countries with sound policies
            that may have some remaining vulnerabilities, to help prevent and
            insure against future crises, and it continues to enhance the tools
            available for crisis prevention.
          </p>
          <p>
            In broad terms, the IMF has two types of lending: loans provided at
            nonconcessional interest rates and loans provided to low-income
            countries on concessional terms. Concessional loans currently bear
            no interest.
          </p>
        </div>

        <span className="anchor" id="lending-map-anchor" />
        <div className="container container--full" style={{ marginBottom: 60 }}>
          <LendingMap />
        </div>

        <div className="container md:container--small text-block">
          <p>
            From the outset of the COVID-19 pandemic, the IMF has responded with
            unprecedented speed and magnitude, making use of its current lending
            capacity of about $1 trillion.¹
          </p>
          <p>
            This response has entailed provision of financial assistance to
            countries with urgent or potential balance of payments needs, with
            the aim of helping protect the lives and livelihoods of people,
            especially the most vulnerable. The Executive Board also temporarily
            streamlined internal processes early in the crisis to allow the IMF
            to respond more quickly to members’ requests for emergency
            assistance—and in many cases made emergency financing available
            within weeks of receiving a request.² In addition, the Executive
            Board temporarily suspended the application of existing high-access
            procedures for Rapid Credit Facility requests.³
          </p>
          <p>
            Policy safeguards were introduced in August 2020 to help mitigate
            financial risks from a member having combined high access to both
            PRGT and General Resources Account (GRA) lending facilities.
            Safeguards now apply to any IMF member with combined access to GRA
            and PRGT resources that exceeds quota-based thresholds set at the
            same level that triggers the exceptional access framework of the
            GRA.
          </p>
          <p>
            In addition, to accommodate high demand for IMF lending resulting
            from the crisis, the Executive Board temporarily increased (1) the
            annual access limit for the IMF’s GRA that triggers application of
            the exceptional access framework and (2) both the annual and
            cumulative access limits on concessional lending through the PRGT
            (see GRA and PRGT agreement details on Access Limits in the{" "}
            <Link to="#lending-map-anchor">interactive lending map</Link>{" "}
            above).
          </p>
          <AppendixWaypoint page={PAGESLUG} id={2} />
          <p>
            Demand for IMF emergency financing tapered off beginning in the
            third quarter of 2020, and some borrowers have moved toward
            multiyear upper-credit-tranche-quality arrangements. In addition,
            the IMF provided grants for debt service relief to its poorest and
            most vulnerable members affected by the COVID-19 pandemic.
          </p>
          <p>
            Between May 1, 2020, and April 30, 2021, the IMF’s financial
            assistance focused on the following areas:
          </p>
          <ol className="text-block__list text-block__list--ordered">
            <li>
              <header>Emergency financing under the RFI and RCF</header>
              <AppendixWaypoint page={PAGESLUG} id={3} />
              <p>
                The IMF received a record number of requests for emergency
                financing—from 39 countries (about $17 billion, of which $6
                billion was disbursed to 26 low-income countries). The Executive
                Board temporarily doubled the access limits to the emergency
                financing facilities: the Rapid Credit Facility (RCF) and Rapid
                Financing Instrument (RFI) (see RCF and RFI agreement details on
                Access Limits in the{" "}
                <Link to="#lending-map-anchor">interactive lending map</Link>{" "}
                above).
              </p>
            </li>
            <li>
              <header>Building on existing lending arrangements</header>
              <p>
                The IMF also augmented existing arrangements to accommodate
                urgent new needs arising from the pandemic within the context of
                the ongoing policy dialogue. Between May 1, 2020, and April 30,
                2021, the Executive Board approved augmentation of arrangements
                with nine members.
              </p>
            </li>
            <li>
              <header>
                New lending arrangements, including precautionary arrangements:
              </header>
              <p>
                Between May 1, 2020 and April 30, 2021, the Executive Board
                approved eight new nonprecautionary IMF-supported arrangements
                with seven countries. In addition, four precautionary
                arrangements—three Flexible Credit Lines and one Precautionary
                and Liquidity Line—were made available to members.
              </p>
            </li>
            <li>
              <header>Debt service relief</header>
              <AppendixWaypoint page={PAGESLUG} id={4} />
              <p>
                The Catastrophe Containment and Relief Trust (CCRT) allows the
                IMF to provide grants for debt relief to the poorest and most
                vulnerable member countries hit by catastrophic natural or
                public health disasters. It was enhanced in March 2020 and was
                subsequently used to provide debt service relief on a grant
                basis to the IMF's poorest members affected by the COVID-19
                pandemic. In total, 29 eligible countries have received debt
                service relief of close to SDR 520 million in three tranches,
                which were approved by the Executive Board on April 13, 2020;
                October 2, 2020; and April 1, 2021 (see CCRT agreement details
                in{" "}
                <a href={`${withPrefix("/")}downloads/table_2.1.pdf`}>
                  this table
                </a>
                ).
              </p>
            </li>
            <li>
              <header>Debt relief under the HIPC Initiative</header>
              <AppendixWaypoint page={PAGESLUG} id={5} />
              <p>
                On March 25, 2020, following Somalia’s clearance of its arrears
                to the IMF, the Executive Board determined that Somalia was
                qualified for debt relief under the enhanced Heavily Indebted
                Poor Countries (HIPC) Initiative and that Somalia had reached
                its HIPC decision point. By the end of April 2021, the Executive
                Board had approved two interim assistance payments to Somalia in
                the total amount of SDR 1.791 million to cover its financial
                obligations falling due during the periods March 25, 2020–March
                24, 2021, and March 25, 2021– March 24, 2022. On March 26, 2021,
                the Executive Board agreed that Sudan⁴ was eligible for debt
                relief assistance under the enhanced HIPC Initiative based on
                the preliminary assessment.was eligible for debt relief
                assistance under the enhanced HIPC Initiative based on the
                preliminary assessment.
              </p>
            </li>
          </ol>
          <ol className="text-block__footnotes">
            <li>
              Including prepandemic commitments, as of April 30, 2021, total
              undisbursed lending commitments and credit outstanding under the
              IMF’s General Resources Account were about SDR 184 billion; the
              corresponding total under the PRGT, which provides concessional
              lending to low-income countries, was about SDR 14.8 billion.
            </li>
            <li>These emergency pandemic procedures lapsed in October 2020.</li>
            <li>
              High-access procedures require an informal Executive Board session
              based on a short staff note that includes discussion of program
              strength, capacity to repay, and debt vulnerabilities. The
              high-access procedures are triggered when (1) a request for IMF
              financing brings total access to more than 180 percent of a
              country’s quota over a 36-month period or (2) total outstanding
              credit from the PRGT exceeds or is projected to exceed 225 percent
              of a country’s quota. In March 2021, these high-access thresholds
              were temporarily increased to 240 percent of quota for the “flow
              trigger” through the end of 2023 and 300 percent for the “stock
              trigger” for the period through the end of June 2021.
            </li>
            <li>
              The Executive Boards of the IMF and World Bank approved Sudan’s
              eligibility for debt relief under the enhanced Heavily Indebted
              Poor Countries Initiative on June 29, 2021 (after this report was
              finalized). For more information, visit{" "}
              <a href="https://www.imf.org/sudan">www.imf.org/sudan</a>.
            </li>
          </ol>
        </div>
      </section>
      <ReadNext sectionID="what-we-do" pageID="capacity-development" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
